import type { HsBoundingBoxView, HsDisposable, HsFrustumRequestInfo, HsFrustumView } from '@sibe/hyperscene';
import type { HsHlodTileset, HsPhotogrammetryLoadStatus } from '@sibe/hyperscene/sources/hlod';
import type {
  HsDocumentInterceptor,
  HsFrustumInterceptor,
  HsFrustumRequestDetails,
  HsProbePointCloudBudget,
  HsProbePointCloudSnapshot,
  HsViewerProbe,
  HsViewerProbePhase,
} from './viewer-probe.interface';

/** What the probe reads of a drawn object. */
export interface HsProbeObject {
  readonly visible: boolean;
  readonly isDeleted: boolean;
}

/** What the probe reads of the camera; `HsPerspectiveCamera` fits. */
export interface HsProbeCamera {
  readonly worldMatrix: { readonly values: ArrayLike<number> };
  readonly worldMatrixInverse: { readonly values: ArrayLike<number> };
  readonly projectionMatrix: { readonly values: ArrayLike<number> };
}

/** A drawn point cloud block: local xyz triples and its column-major world matrix. */
export interface HsProbePointCloudBlock {
  points: ArrayLike<number>;
  matrix: ArrayLike<number>;
  /** Rendered point size in pixels, when blocks are sized apart (adaptive point sizes). */
  pointSize?: number;
}

/** The host's answers; everything optional has a neutral default. */
export interface HsViewerProbeHost {
  phase(): HsViewerProbePhase;
  error(): string | null;
  sceneId(): string | null;
  /** Host work in flight, besides the HLOD work the probe finds itself. */
  busyReasons?(): string[];
  /** The visible lists of the last culling pass, or null before the viewer exists. */
  visibleObjects(): {
    readonly unsortedObjects: readonly HsProbeObject[];
    readonly sortedObjects: readonly HsProbeObject[];
  } | null;
  loadedTriangles(): number;
  renderedTriangles(): number;
  camera(): HsProbeCamera;
  gl(): WebGL2RenderingContext | null;
  hlodTilesets?(): readonly HsHlodTileset<unknown>[];
  /** `HsPhotogrammetrySource.status()`; the probe turns it into the `hlod-*` busy reasons. */
  photogrammetryStatus?(): HsPhotogrammetryLoadStatus | null;
  /** Whether the scene has point cloud blocks at all; snapshots are null without them. */
  hasPointClouds?(): boolean;
  /** Keys of the point cloud blocks drawn now. */
  drawnPointCloudKeys?(): string[];
  /** A drawn block by key, or null when it is not drawn. */
  pointCloudBlock?(key: string): HsProbePointCloudBlock | null;
  pointSize?(): number;
  pointCloudBudget?(): HsProbePointCloudBudget | null;
  /** Scene bounds from the API, or null; the probe then uses the HLOD tileset bounds. */
  sceneBounds?(): Promise<HsBoundingBoxView | null>;
  applyCameraView(view: { position: number[]; direction: number[] }): void;
  setNavigating(active: boolean): void;
  requestRender(): void;
}

/** A probe and the hooks its host calls: after each frame update and around its Frustum and document requests. */
export interface HsViewerProbeHandle {
  readonly probe: HsViewerProbe;
  /** Reports a frame update and its duration to `onFrame` listeners. */
  frame(updateMs: number): void;
  /** Whether anyone listens to frames, so hosts skip timing work otherwise. */
  readonly listensToFrames: boolean;
  /** Runs a Frustum request through the benchmark's interceptors (recording, replay). */
  frustum(
    sceneId: string,
    request: HsFrustumRequestInfo,
    details: HsFrustumRequestDetails,
    call: () => Promise<HsFrustumView>
  ): Promise<HsFrustumView>;
  /** Runs a document download through the benchmark's interceptors. */
  documents(
    documentIds: string[],
    call: (documentIds: string[]) => Promise<Map<string, ArrayBuffer>>
  ): Promise<Map<string, ArrayBuffer>>;
}

/**
 * Builds the benchmark probe of a viewer host. The probe assembles what the harness reads (busy reasons,
 * visible objects, point cloud snapshots, scene bounds) from the host's answers and chains the harness's
 * request interceptors; the host forwards frames and requests through the returned hooks.
 */
export function hsCreateViewerProbe(host: HsViewerProbeHost): HsViewerProbeHandle {
  const frameListeners: Set<(updateMs: number) => void> = new Set();
  const frustumInterceptors: HsFrustumInterceptor[] = [];
  const documentInterceptors: HsDocumentInterceptor[] = [];
  const tilesets = (): readonly HsHlodTileset<unknown>[] => host.hlodTilesets?.() ?? [];
  const register = <T>(list: T[], item: T): HsDisposable => {
    list.push(item);
    return {
      dispose: (): void => {
        const index: number = list.indexOf(item);
        if (index >= 0) {
          list.splice(index, 1);
        }
      },
    };
  };

  const probe: HsViewerProbe = {
    version: 1,
    phase: () => host.phase(),
    error: () => host.error(),
    sceneId: () => host.sceneId(),
    busyReasons: (): string[] => {
      const reasons: string[] = [...(host.busyReasons?.() ?? [])];
      const status: HsPhotogrammetryLoadStatus | null = host.photogrammetryStatus?.() ?? null;
      if (status === null) {
        return reasons;
      }
      if (status.manifestsLoading > 0) {
        reasons.push('hlod-manifest');
      }
      if (
        status.subtreesLoading + status.nodesLoading + status.nodesQueued + status.nodesDecoded > 0 ||
        status.requestsInFlight > 0
      ) {
        reasons.push('hlod-nodes');
      }
      if (!status.rootReady) {
        reasons.push('hlod-root');
      }
      if (status.nodesRetrying > 0) {
        reasons.push('hlod-retry');
      }
      return reasons;
    },
    onFrame: (listener: (updateMs: number) => void): HsDisposable => {
      frameListeners.add(listener);
      return { dispose: (): void => void frameListeners.delete(listener) };
    },
    collectVisible: (into: Set<unknown>): void => {
      const visible: ReturnType<HsViewerProbeHost['visibleObjects']> = host.visibleObjects();
      if (visible === null) {
        return;
      }
      for (const list of [visible.unsortedObjects, visible.sortedObjects]) {
        for (const object of list) {
          if (object.visible && !object.isDeleted) {
            into.add(object);
          }
        }
      }
    },
    loadedTriangles: () => host.loadedTriangles(),
    renderedTriangles: () => host.renderedTriangles(),
    cameraMatrix: () => host.camera().worldMatrix.values,
    hlodTilesets: tilesets,
    sceneBounds: async (): Promise<HsBoundingBoxView | null> => {
      try {
        const bounds: HsBoundingBoxView | null = (await host.sceneBounds?.()) ?? null;
        if (bounds !== null) {
          return bounds;
        }
      } catch {
        // Fall back to the HLOD bounds below.
      }
      const boxes: HsBoundingBoxView[] = tilesets().map((tileset: HsHlodTileset<unknown>) => tileset.worldBounds());
      if (boxes.length === 0) {
        return null;
      }
      return {
        min: [0, 1, 2].map((axis: number) => Math.min(...boxes.map((box: HsBoundingBoxView) => box.min[axis]))),
        max: [0, 1, 2].map((axis: number) => Math.max(...boxes.map((box: HsBoundingBoxView) => box.max[axis]))),
      };
    },
    applyCameraView: (view: { position: number[]; direction: number[] }) => host.applyCameraView(view),
    setNavigating: (active: boolean) => host.setNavigating(active),
    requestRender: () => host.requestRender(),
    canvas: (): HTMLCanvasElement | null => (host.gl()?.canvas as HTMLCanvasElement | undefined) ?? null,
    interceptFrustum: (interceptor: HsFrustumInterceptor) => register(frustumInterceptors, interceptor),
    interceptDocuments: (interceptor: HsDocumentInterceptor) => register(documentInterceptors, interceptor),
    drawnPointCloudKeys: () => host.drawnPointCloudKeys?.() ?? [],
    pointCloudBudget: () => host.pointCloudBudget?.() ?? null,
    pointCloudSnapshot: (keys?: readonly string[]): HsProbePointCloudSnapshot | null => {
      const gl: WebGL2RenderingContext | null = host.gl();
      if (gl === null || host.hasPointClouds?.() !== true) {
        return null;
      }
      const blocks: HsProbePointCloudSnapshot['blocks'] = [];
      for (const key of keys ?? host.drawnPointCloudKeys?.() ?? []) {
        const block: HsProbePointCloudBlock | null = host.pointCloudBlock?.(key) ?? null;
        if (block !== null) {
          blocks.push(
            block.pointSize === undefined
              ? { key, points: block.points, matrix: block.matrix }
              : { key, points: block.points, matrix: block.matrix, pointSize: block.pointSize }
          );
        }
      }
      const camera: HsProbeCamera = host.camera();
      return {
        width: gl.drawingBufferWidth,
        height: gl.drawingBufferHeight,
        pointSize: host.pointSize?.() ?? 1,
        projection: camera.projectionMatrix.values,
        view: camera.worldMatrixInverse.values,
        blocks,
      };
    },
  };

  return {
    probe,
    frame: (updateMs: number): void => frameListeners.forEach((listener: (ms: number) => void) => listener(updateMs)),
    get listensToFrames(): boolean {
      return frameListeners.size > 0;
    },
    frustum: (
      sceneId: string,
      request: HsFrustumRequestInfo,
      details: HsFrustumRequestDetails,
      call: () => Promise<HsFrustumView>
    ): Promise<HsFrustumView> => {
      const run = (index: number): Promise<HsFrustumView> =>
        index < frustumInterceptors.length
          ? frustumInterceptors[index](() => run(index + 1), sceneId, request, details)
          : call();
      return run(0);
    },
    documents: (
      documentIds: string[],
      call: (ids: string[]) => Promise<Map<string, ArrayBuffer>>
    ): Promise<Map<string, ArrayBuffer>> => {
      const run = (index: number, ids: string[]): Promise<Map<string, ArrayBuffer>> =>
        index < documentInterceptors.length
          ? documentInterceptors[index]((next: string[]) => run(index + 1, next), ids)
          : call(ids);
      return run(0, documentIds);
    },
  };
}
